// src/components/SitzungPraesentation.jsx
// Optionale PDF-Präsentation im Sitzungsmodus (US-SITZ-003, ADR 0059):
// Upload zerlegt die PDF-Datei clientseitig in Einzelseiten ("Folien"), die als
// Mid-Resolution-Bilder im Blob Storage abgelegt werden. Jede Folie erscheint
// als eigene Zeile; daneben eine sofort editierbare, leere Pendenz ("+ Pendenz"
// legt weitere an). Zwischen den Folien lassen sich ebenfalls Pendenzen
// erfassen – diese Erfassungsfelder sind per default immer sichtbar
// (Nutzerwunsch 2026-09-16), nicht mehr eingeklappt.
//
// Lebt bewusst als eigene Komponente statt in Pendenzen.jsx (das Dateiende ist
// bereits sehr gross) und wird nur gerendert, während der Sitzungsmodus aktiv
// ist. Wiederverwendet ResponsibleSelect/verantwortlichDisplay(Extern) aus
// Pendenzen.jsx, damit die Verantwortliche-Logik nicht dupliziert wird.
import React, { useEffect, useState, useCallback } from "react";
import {
  getPraesentation,
  uploadPraesentation,
  deletePraesentation,
} from "../services/azureSitzungFolienService";
import { createPendenz } from "../services/azurePendenzenService";
import {
  ResponsibleSelect,
  verantwortlichDisplay,
  verantwortlichDisplayExtern,
} from "./Pendenzen";
import RichTextEditor from "./RichTextEditor";
import { sanitizeRichText, isRichTextEmpty } from "../utils/richText";
import { useAuth } from "../hooks/useAuth";

const ART_OPTIONS = [
  { value: "P", label: "Pendenz" },
  { value: "E", label: "Entscheid" },
  { value: "N", label: "Notiz" },
];

// --- Eine bestehende, der Folie/dem Zwischenraum zugeordnete Pendenz --------
const AnchoredPendenzChip = ({ pendenz, onOpen }) => (
  <button
    type="button"
    className={`sitzung-praesentation__chip sitzung-praesentation__chip--${pendenz.typ}`}
    onClick={() => onOpen(pendenz)}
    title="Zum Bearbeiten anklicken"
  >
    <span className={`art-badge art-badge--${pendenz.typ}`}>{pendenz.typ}</span>
    <span
      className="rte-content"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: sanitizeRichText(pendenz.beschreibung) }}
    />
    {pendenz.verantwortlich && (
      <span className="sitzung-praesentation__chip-resp">{pendenz.verantwortlich}</span>
    )}
  </button>
);

// --- Inline-Schnellerfassung für eine Folie oder einen Zwischenraum --------
// Bleibt nach dem Speichern leer stehen (statt zu verschwinden), damit "mehr
// Pendenzen hinzufügen" einfach ein weiteres Mal Beschreibung eintippen + auf
// "Hinzufügen" klicken bedeutet. Wird immer ausgeklappt angezeigt (auch
// zwischen Folien) – Nutzerwunsch: Notizen sollen per default immer sichtbar
// sein, kein zusätzlicher Klick zum Aufklappen nötig.
const AnchoredQuickAdd = ({
  anchor,
  sitzungId,
  projectUuid,
  responsibleOptions,
  crmOptions,
  onCreateRequest,
  onLinkExisting,
  onCreated,
}) => {
  const { user } = useAuth();
  const [typ, setTyp] = useState("P");
  const [beschreibung, setBeschreibung] = useState("");
  const [verantwortlichIds, setVerantwortlichIds] = useState([]);
  const [editorKey, setEditorKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const commit = async () => {
    if (isRichTextEmpty(beschreibung) || saving) return;
    setSaving(true);
    setError(null);
    try {
      await createPendenz(projectUuid, {
        typ,
        kategorie: "Org",
        beschreibung,
        verantwortlichIds,
        verantwortlich: verantwortlichDisplay(verantwortlichIds, responsibleOptions),
        verantwortlichExtern: verantwortlichDisplayExtern(verantwortlichIds, responsibleOptions),
        terminSoll: "",
        terminNeu: "",
        status: "offen",
        kommentar: "",
        checkliste: [],
        themen: [],
        sichtbarExtern: true,
        sitzungId,
        praesentationAnchor: anchor,
        erstelltVon: user?.email || user?.userDetails || "",
      });
      setTyp("P");
      setBeschreibung("");
      setVerantwortlichIds([]);
      setEditorKey((k) => k + 1);
      await onCreated?.();
    } catch (err) {
      setError(err.message || "Die Pendenz konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  };

  // Verlässt der Fokus das ganze Erfassungsfeld (Klick/Tab woanders hin,
  // Sitzungsmodus beenden), wird sofort gespeichert – analog der
  // Schnellerfassungszeile der Hauptliste. Ein Klick auf einen Button
  // INNERHALB des Feldes (Art, Verantwortliche, "Hinzufügen") verlässt das
  // Feld nicht und löst daher keinen doppelten Save aus.
  const handleBlur = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    commit();
  };

  return (
    <div className="sitzung-praesentation__quickadd" onBlur={handleBlur}>
      {error && (
        <div className="pendenzen__error" role="alert" onClick={() => setError(null)}>
          {error}
        </div>
      )}
      <div className="sitzung-praesentation__quickadd-row">
        <select value={typ} onChange={(e) => setTyp(e.target.value)} aria-label="Art">
          {ART_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.value}
            </option>
          ))}
        </select>
        <ResponsibleSelect
          options={responsibleOptions}
          selectedIds={verantwortlichIds}
          onChange={setVerantwortlichIds}
          onCreateRequest={onCreateRequest}
          crmOptions={crmOptions}
          onLinkExisting={onLinkExisting}
          singleOnly
        />
      </div>
      <RichTextEditor
        key={editorKey}
        value={beschreibung}
        onChange={setBeschreibung}
        placeholder="Notiz erfassen…"
      />
      <div className="sitzung-praesentation__quickadd-actions">
        <button
          type="button"
          className="pendenzen__btn pendenzen__btn--primary"
          onClick={commit}
          disabled={isRichTextEmpty(beschreibung) || saving}
        >
          {saving ? "Speichern..." : "Hinzufügen"}
        </button>
      </div>
    </div>
  );
};

const SitzungPraesentation = ({
  sitzungId,
  projectUuid,
  pendenzen,
  responsibleOptions,
  crmOptions,
  onCreateRequest,
  onLinkExisting,
  onDataChanged,
  onOpenPendenz,
  readOnly = false,
}) => {
  const { user } = useAuth();
  const [praesentation, setPraesentation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!sitzungId) return;
    try {
      setLoading(true);
      setPraesentation(await getPraesentation(sitzungId));
    } catch (err) {
      setError(err.message || "Die Präsentation konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [sitzungId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // gleiche Datei erneut auswählbar
    if (!file) return;
    try {
      setError(null);
      setUploading(true);
      setUploadProgress({ current: 0, total: 0 });
      const result = await uploadPraesentation(
        { projectUuid, sitzungId, file, hochgeladenVon: user?.email || user?.userDetails || "" },
        (current, total) => setUploadProgress({ current, total })
      );
      setPraesentation(result);
      await onDataChanged?.();
    } catch (err) {
      setError(err.message || "Die Präsentation konnte nicht hochgeladen werden.");
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (
      !window.confirm(
        "Präsentation wirklich entfernen? Die Folien werden gelöscht; zugeordnete Pendenzen bleiben erhalten, verlieren aber die Folienzuordnung."
      )
    ) {
      return;
    }
    try {
      setError(null);
      await deletePraesentation(projectUuid, sitzungId);
      setPraesentation({ meta: null, folien: [] });
      await onDataChanged?.();
    } catch (err) {
      setError(err.message || "Die Präsentation konnte nicht entfernt werden.");
    }
  };

  const anchoredTo = (anchor) => pendenzen.filter((p) => p.praesentationAnchor === anchor);

  const renderGap = (anchor) => (
    <div className="sitzung-praesentation__gap">
      {anchoredTo(anchor).map((p) => (
        <AnchoredPendenzChip key={p.id} pendenz={p} onOpen={onOpenPendenz} />
      ))}
      {!readOnly && (
        <AnchoredQuickAdd
          anchor={anchor}
          sitzungId={sitzungId}
          projectUuid={projectUuid}
          responsibleOptions={responsibleOptions}
          crmOptions={crmOptions}
          onCreateRequest={onCreateRequest}
          onLinkExisting={onLinkExisting}
          onCreated={onDataChanged}
        />
      )}
    </div>
  );

  if (loading) {
    return <p className="sitzung-praesentation__hint">Präsentation wird geladen…</p>;
  }

  return (
    <div className="sitzung-praesentation">
      {error && (
        <div className="pendenzen__error" role="alert" onClick={() => setError(null)}>
          {error}
        </div>
      )}

      {!praesentation?.meta ? (
        !readOnly && (
          <div className="sitzung-praesentation__upload">
            <label className="pendenzen__btn">
              PDF-Präsentation hochladen…
              <input
                type="file"
                accept="application/pdf"
                onChange={handleFileSelected}
                disabled={uploading}
                hidden
              />
            </label>
            {uploading && (
              <span className="sitzung-praesentation__hint">
                Folie {uploadProgress.current} von {uploadProgress.total || "?"} wird verarbeitet…
              </span>
            )}
          </div>
        )
      ) : (
        <>
          <div className="sitzung-praesentation__header">
            <span>
              {praesentation.meta.dateiname} · {praesentation.meta.seitenAnzahl} Folien
            </span>
            {!readOnly && (
              <div className="sitzung-praesentation__header-actions">
                <label className="pendenzen__btn">
                  Ersetzen…
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={handleFileSelected}
                    disabled={uploading}
                    hidden
                  />
                </label>
                <button
                  type="button"
                  className="pendenzen__btn pendenzen__btn--danger"
                  onClick={handleRemove}
                  disabled={uploading}
                >
                  Präsentation entfernen
                </button>
              </div>
            )}
            {uploading && (
              <span className="sitzung-praesentation__hint">
                Folie {uploadProgress.current} von {uploadProgress.total || "?"} wird verarbeitet…
              </span>
            )}
          </div>

          {renderGap("gap:start")}

          {praesentation.folien.map((f) => (
            <React.Fragment key={f.id}>
              <div className="sitzung-praesentation__slide-row">
                <img
                  className="sitzung-praesentation__slide-img"
                  src={f.url}
                  alt={`Folie ${f.seite}`}
                  loading="lazy"
                />
                <div className="sitzung-praesentation__slide-pendenzen">
                  <h5>Folie {f.seite}</h5>
                  {anchoredTo(`slide:${f.id}`).map((p) => (
                    <AnchoredPendenzChip key={p.id} pendenz={p} onOpen={onOpenPendenz} />
                  ))}
                  {!readOnly && (
                    <AnchoredQuickAdd
                      anchor={`slide:${f.id}`}
                      sitzungId={sitzungId}
                      projectUuid={projectUuid}
                      responsibleOptions={responsibleOptions}
                      crmOptions={crmOptions}
                      onCreateRequest={onCreateRequest}
                      onLinkExisting={onLinkExisting}
                      onCreated={onDataChanged}
                    />
                  )}
                </div>
              </div>
              {renderGap(`gap:${f.id}`)}
            </React.Fragment>
          ))}
        </>
      )}
    </div>
  );
};

export default SitzungPraesentation;
